import {useMemo} from 'react';
import {GetFormattedBulkPointModifiers} from '@/models/BulkModifierModel';
import {useDataTable} from '@/hooks/useDataTable-hook';
import {buildQueryParams} from '@/utils/commons';
import {
  getBulkModifierCsvFile,
  getBulkModifiers
} from '@/services/bulkModifiers';
import {BulkModifiersListColConfig} from '@/services/MockData/BulkModifiersList/BulkModifiersListColConfig';
import {useQueries} from '@tanstack/react-query';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {logger} from '@/helpers/logger';

const initialSortConfig = {
  column: BulkModifiersListColConfig[2].name_of_db_column,
  direction: 'desc'
};

export const useBulkModifiers = () => {
  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll
  } = useDataTable(0, false, false, initialSortConfig);

  // Move search params to request body and only use query params for pagination/sorting
  const queryParams = useMemo(() => {
    return buildQueryParams({
      ...(sortingObj?.column && {
        sortBy: sortingObj.column,
        order: sortingObj.direction
      }),
      pageSize: 10,
      page: pageIndex || 0
    });
  }, [sortingObj?.column, sortingObj?.direction, pageIndex]);

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['BULK_MODIFIERS', queryParams, searchArr],
        queryFn: () => getBulkModifiers(queryParams, searchArr)
      }
    ]
  });

  const resultsData = useMemo(() => data || {}, [data]);
  const bulkModifiersData = useMemo(
    () => GetFormattedBulkPointModifiers(resultsData),
    [resultsData]
  );

  const onDownloadBtnClick = async (selectedIds, excludeIds = []) => {
    const requestBody = {
      ids: selectedIds,
      excludeIds,
      filters: searchArr
    };

    try {
      const data = await getBulkModifierCsvFile(queryParams, requestBody);
      const fileName = `BulkModifier_${getDateWithTimeString()}`;
      downloadCSVFiles(data, fileName);
    } catch (error) {
      logger.error('Error downloading CSV file:', error);
    }
  };

  return {
    bulkModifiersData,
    bulkModifiersColConfigData: BulkModifiersListColConfig,
    isLoading: isLoading,
    isFetching: isFetching,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll,
    onDownloadBtnClick
  };
};
